import { Item, select } from "gamedata/GameData";
import { ReactElement, useState } from "react";
import { Link, useLoaderData, useNavigate, useParams } from "react-router";
import { InputMD } from "~/components/form/InputMd";
import { AdenaValue } from "~/components/item/Adena";
import { CraftLink } from "~/components/item/CraftLink";
import { CrystalName, CrystalValue } from "~/components/item/Crystal";
import { CrystalCalc } from "~/components/item/CrystalCalc";
import { ItemInfoBox } from "~/components/item/ItemInfoBox";
import { ItemLink } from "~/components/item/ItemLink";
import { NpcHpRate } from "~/components/npc/NpcHpRate";
import { NpcLink } from "~/components/npc/NpcLink";
import { QuestLink } from "~/components/quest/QuestLink";
import { QuestProfList } from "~/components/quest/QuestProfList";
import { QuestRaceList } from "~/components/quest/QuestRaceList";
import { QuestRewardList } from "~/components/quest/QuestRewardList";
import { Comments, getComments } from "~/components/site/Comments";
import { MdContent } from "~/components/site/MdContent";
import { EntityTooltip } from "~/components/site/Tooltip";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { Facts } from "~/components/ui/Facts";
import { Heading2, Heading3, Heading4 } from "~/components/ui/Heading";
import { getIconFullUrl, Icon, Icon32 } from "~/components/ui/Icon";
import { InfoBox } from "~/components/ui/InfoBox";
import { PageTitle } from "~/components/ui/PageTitle";
import { Table } from "~/components/ui/Table";
import type { TabSpec } from "~/components/ui/Tabs";
import { Tabs } from "~/components/ui/Tabs";
import { stripSlashes } from "~/util/common";
import { getDescription, itemMdPage, itemName } from "~/util/item";
import { GameVer, tt, useRealm, useT, useTurl } from "~/util/Lang";
import { callApi, type MetaFunc, type OurLoaderArgs } from "~/util/Loaders";
import { getLocList } from "~/util/loc";
import { getNpcList } from "~/util/npc";
import { createSorting, SortBy, SortColumnName } from "~/util/Sorting";
import { logPageView } from "~/util/stats";
import { homePage } from "~/util/titles";
import { editPageApi } from "../api/mdPages.pages";
import { z } from "zod";
import { NotFoundInfo } from "~/components/site/NotFoundInfo";

type ItemDropSort = {
  npc: {
    id: number;
    race: {
      raceName: string;
      icon: string;
      name: string;
    };
    type: string;
    npcName: string;
    name: string;
    level: number | null;
    nick: string;
    nickColor: "default" | "quest" | "raid";
    locs: {
      id: number;
      name: string;
      locName: string;
    }[];
    skills: {
      id: number;
      skillName: string;
      level: number | null;
      name: string;
      icon: string;
    }[];
  };
  chance: number;
  countMin: number;
  countMax: number;
};

type ItemShopSort = {
  npc: {
    id: number;
    race: {
      raceName: string;
      icon: string;
      name: string;
    };
    type: string;
    npcName: string;
    name: string;
    level: number | null;
    nick: string;
    nickColor: "default" | "quest" | "raid";
    locs: {
      id: number;
      name: string;
      locName: string;
    }[];
  };
  tax: number;
};

type ItemContainInSort = {
  item: {
    id: number;
    itemName: string;
    name: string;
    icon: string;
  };
  count: number;
  chance: number;
};

const SORTER_DROP = createSorting<ItemDropSort>({
  name: (a, b) => (a.npc.name ?? "").localeCompare(b.npc.name ?? ""),
  lvl: (a, b) => (a.npc.level ?? 0) - (b.npc.level ?? 0),
  chance: (a, b) => (a.chance ?? 0) - (b.chance ?? 0),
  count: (a, b) =>
    (a.countMin + a.countMax) / 2 - (b.countMin + b.countMax) / 2,
});

const SORTER_SHOP = createSorting<ItemShopSort>({
  name: (a, b) => (a.npc.name ?? "").localeCompare(b.npc.name ?? ""),
  tax: (a, b) => (a.tax ?? 0) - (b.tax ?? 0),
});

const SORTER_CONTAIN_IN = createSorting<ItemContainInSort>({
  chance: (a, b) => (a.chance ?? 0) - (b.chance ?? 0),
  count: (a, b) => (a.count ?? 0) - (b.count ?? 0),
});

type ItemAlt = {
  name: string;
  icon: string;
  itemName: string;
  gver: GameVer;
};

type LoaderDataOk = Awaited<ReturnType<typeof makeData>>;

async function loadData(
  la: OurLoaderArgs,
): Promise<
  { status: "404"; alts: ItemAlt[] } | { status: "ok"; data: LoaderDataOk }
> {
  const t = tt.bind(null, la.realm.lang);
  const params = Params.parse(la.params);
  const item = la.gd.items.get(params.itemId);

  if (item == null) {
    la.setStatusCode(404);
    const alts: ItemAlt[] = [];
    for (const altGD of Object.values(la.gd.all_gd!)) {
      const altItem = altGD.items.get(params.itemId);
      if (altItem) {
        alts.push({
          name: t(altItem.name),
          icon: altItem.icon,
          itemName: altItem.itemName,
          gver: altGD.gver,
        });
      }
    }
    return { status: "404", alts };
  }

  return { status: "ok", data: await makeData(la, item) };
}

const Params = z.object({ itemId: z.string() });
async function makeData(la: OurLoaderArgs, item: Item) {
  const t = tt.bind(null, la.realm.lang);
  const params = Params.parse(la.params);

  // Log
  logPageView(la);
  //

  const data = {
    item: select(item, {
      id: true,
      icon: true,
      itemName: true,
      name: { en: true, ru: true },
      addName: { en: true, ru: true },
      desc: t,
      crystalType: true,
      crystalCount: true,
      type: true,
      slotBitType: true,
      armorType: true,
      weaponType: true,
      physicalDamage: true,
      magicalDamage: true,
      attackSpeed: true,
      shieldDefense: true,
      shieldDefenseRate: true,
      avoidModify: true,
      physicalDefense: true,
      magicalDefense: true,
      soulshotCount: true,
      spiritshotCount: true,
      defaultPrice: true,
      weight: true,
      mpBonus: true,
      durability: true,
      inRecipe: {
        select: { itemName: true, name: t, addName: t, id: true, icon: true },
      },
      exchange_from: {
        select: {
          requiredItems: {
            select: {
              count: true,
              item: {
                id: true,
                itemName: true,
                icon: true,
                name: t,
              },
            },
          },
          resultItems: {
            select: {
              count: true,
              item: {
                id: true,
                itemName: true,
                icon: true,
                name: t,
              },
            },
          },
          npcs: {
            select: {
              id: true,
              npcName: true,
              name: t,
              level: true,
              nick: t,
              nickColor: true,
              locs: { select: { id: true, name: t, locName: true } },
            },
          },
        },
      },
      exchange_for: {
        select: {
          requiredItems: {
            select: {
              count: true,
              item: {
                id: true,
                itemName: true,
                icon: true,
                name: t,
              },
            },
          },
          resultItems: {
            select: {
              count: true,
              item: {
                id: true,
                itemName: true,
                icon: true,
                name: t,
              },
            },
          },
          npcs: {
            select: {
              id: true,
              npcName: true,
              name: t,
              level: true,
              nick: t,
              nickColor: true,
              locs: { select: { id: true, name: t, locName: true } },
            },
          },
        },
      },
      inQuests: {
        select: {
          id: true,
          uname: true,
          name: t,
          minLevel: true,
          maxLevel: true,
          reward: { exp: true, sp: true, items: { select: { icon: true } } },
          requirements: {
            category: true,
            items: { select: { select: { name: t } } },
            occupation: {
              select: { id: true, profName: true, name: t },
            },
            races: {
              select: {
                raceName: true,
                icon: true,
                name: t,
              },
            },
          },
        },
      },
      questReward: {
        select: {
          id: true,
          uname: true,
          name: t,
          minLevel: true,
          maxLevel: true,
          reward: { exp: true, sp: true, items: { select: { icon: true } } },
          requirements: {
            category: true,
            items: { select: { select: { name: t } } },
            occupation: {
              select: { id: true, profName: true, name: t },
            },
            races: {
              select: {
                raceName: true,
                icon: true,
                name: t,
              },
            },
          },
        },
      },
      recipe: {
        select: {
          materialList: {
            select: {
              item: { id: true, icon: true, name: t, itemName: true },
              count: true,
            },
          },
          productList: {
            select: {
              item: { id: true, icon: true, name: t, itemName: true },
              count: true,
            },
          },
          successRate: true,
          mpConsume: true,
          item: { id: true, itemName: true, name: t, icon: true },
        },
      },
      dropList: {
        select: {
          npc: {
            id: true,
            race: { raceName: true, icon: true, name: t },
            type: true,
            npcName: true,
            name: t,
            level: true,
            nick: t,
            nickColor: true,
            locs: {
              select: { id: true, name: t, locName: true },
            },
            skills: {
              select: {
                id: true,
                skillName: true,
                level: true,
                name: t,
                icon: true,
              },
            },
          },
          chance: true,
          countMin: true,
          countMax: true,
        },
      },
      spoilList: {
        select: {
          npc: {
            id: true,
            race: { raceName: true, icon: true, name: t },
            type: true,
            npcName: true,
            name: t,
            level: true,
            nick: t,
            nickColor: true,
            locs: {
              select: { id: true, name: t, locName: true },
            },
            skills: {
              select: {
                id: true,
                skillName: true,
                level: true,
                name: t,
                icon: true,
              },
            },
          },
          chance: true,
          countMin: true,
          countMax: true,
        },
      },
      herbList: {
        select: {
          npc: {
            id: true,
            race: { raceName: true, icon: true, name: t },
            type: true,
            npcName: true,
            name: t,
            level: true,
            nick: t,
            nickColor: true,
            locs: {
              select: { id: true, name: t, locName: true },
            },
            skills: {
              select: {
                id: true,
                skillName: true,
                level: true,
                name: t,
                icon: true,
              },
            },
          },
          chance: true,
          countMin: true,
          countMax: true,
        },
      },
      sellList: {
        select: {
          npc: {
            id: true,
            race: { raceName: true, icon: true, name: t },
            type: true,
            npcName: true,
            name: t,
            level: true,
            nick: t,
            nickColor: true,
            locs: {
              select: { id: true, name: t, locName: true },
            },
          },
          tax: true,
        },
      },
      product: {
        select: {
          recipe: {
            item: {
              id: true,
              icon: true,
              name: t,
              itemName: true,
              dropList: { select: { chance: true } },
              sellList: { select: { tax: true } },
              spoilList: { select: { chance: true } },
              exchange_from: {
                select: { resultItems: { select: { count: true } } },
              },
              questReward: { select: { id: true } },
            },
            successRate: true,
            level: true,
            materialList: {
              select: {
                item: { id: true, icon: true, name: t, itemName: true },
                count: true,
              },
            },
            productList: {
              select: {
                item: { id: true, icon: true, name: t, itemName: true },
                count: true,
              },
            },
            mpConsume: true,
          },
        },
      },
      sets: {
        select: {
          id: true,
          setName: true,
          icon: true,
          name: t,
          desc: t,
          items: {
            slotHead: {
              select: {
                id: true,
                itemName: true,
                name: t,
                addName: t,
                icon: true,
              },
            },
            slotChest: {
              select: {
                id: true,
                itemName: true,
                name: t,
                addName: t,
                icon: true,
              },
            },
            slotLegs: {
              select: {
                id: true,
                itemName: true,
                name: t,
                addName: t,
                icon: true,
              },
            },
            slotGloves: {
              select: {
                id: true,
                itemName: true,
                name: t,
                addName: t,
                icon: true,
              },
            },
            slotFeet: {
              select: {
                id: true,
                itemName: true,
                name: t,
                addName: t,
                icon: true,
              },
            },
            slotLhand: {
              select: {
                id: true,
                itemName: true,
                name: t,
                addName: t,
                icon: true,
              },
            },
          },
        },
      },
      specialAbility: {
        select: {
          id: true,
          itemName: true,
          name: t,
          icon: true,
          addName: t,
        },
      },
      enchantBonus: {
        select: {
          level: true,
          pAtk: true,
          pDef: true,
          mAtk: true,
          mDef: true,
          crystals: (x) => x,
          chance: true,
        },
      },
      contains: {
        select: {
          items: {
            select: {
              item: {
                id: true,
                itemName: true,
                name: t,
                icon: true,
              },
              count: true,
            },
          },
          chance: true,
        },
      },
      containsIn: {
        select: {
          item: {
            id: true,
            itemName: true,
            name: t,
            icon: true,
          },
          count: true,
          chance: true,
        },
      },
    }),
  };
  return {
    ...data,
    mdPage: await itemMdPage(la, params.itemId),
    comments: await getComments({
      id: data.item.itemName,
      type: "item",
      args: la,
    }),
    authUser: la.req.sess?.user,
  };
}

type LoaderData = Awaited<ReturnType<typeof loader>>;

export const loader = async (la: OurLoaderArgs) => {
  const data = await loadData(la);
  return data;
};

export const meta: MetaFunc<LoaderData> = (cx) => {
  if (cx.data.status === "404") {
    return {
      title: "Item not found - " + homePage(cx.realm),
    };
  }

  const item = cx.data.data.item;
  const name = item.name ?? item.itemName;
  const addName = item.addName;
  const sources = [
    item.product.length && "craft",
    item.exchange_from.length && "multisell",
    item.dropList.length && "drop",
    item.spoilList.length && "spoil",
    item.sellList.length && "sell",
    item.questReward.length && "reward",
  ].filter((x) => x !== 0);
  `${name.ru} ${addName.ru}`.trim();
  const itemNameEn = `${name.en} ${addName.en ? `[${addName.en}]` : ""}`.trim();
  const itemNameRu = `${name.ru} ${addName.ru ? `[${addName.ru}]` : ""}`.trim();
  const itemName = cx.t({
    en: itemNameEn,
    ru: `${itemNameRu} (${itemNameEn})`,
  });
  return {
    title: `${itemName} - ${cx.t({
      en: "Items",
      ru: "Предметы",
    })} - ${homePage(cx.realm)}`,
    description: `${getDescription({ item, sources, realm: cx.realm })}`,
    ogTitle: cx.t({
      en: `Item: ${name.en}`,
      ru: `Предмет: ${name.ru} (${name.en})`,
    }),
    ogImage: item.icon && getIconFullUrl(item.icon, cx.realm.game),
    canonicalUrl: `/items/${item.itemName}`,
  };
};

export default function ItemPage() {
  const t = useT();
  const turl = useTurl();
  const realm = useRealm();
  const params = useParams();
  const nav = useNavigate();
  const loaderData = useLoaderData<typeof loader>();

  const [spoil, setSpoil] = useState<string[]>([]);
  const [herbs, setHerbs] = useState<string[]>([]);
  const [drop, setDrop] = useState<string[]>([]);
  const [shop, setShop] = useState<string[]>([]);
  const [containsIn, setContainsIn] = useState<string[]>([]);

  if (loaderData.status === "404") {
    return (
      <NotFoundInfo
        title={t({ en: "Item not found", ru: "Вещь не найдена" })}
        altsTitle={t({
          en: "This item exists in other gave versions:",
          ru: "Эта вещь доступна в других версиях игры:",
        })}
        alts={loaderData.alts.map((altNpc) => ({
          gver: altNpc.gver,
          node: <ItemLink item={altNpc} />,
        }))}
      />
    );
  }

  const data = loaderData.data;

  function sortBySpoil(): SortBy {
    return {
      value: spoil,
      update: (next) => setSpoil(next),
    };
  }

  function sortByHerbs(): SortBy {
    return {
      value: herbs,
      update: (next) => setHerbs(next),
    };
  }

  function sortByDrop(): SortBy {
    return {
      value: drop,
      update: (next) => setDrop(next),
    };
  }

  function sortByShop(): SortBy {
    return {
      value: shop,
      update: (next) => setShop(next),
    };
  }

  function sortByContainsIn(): SortBy {
    return {
      value: containsIn,
      update: (next) => setContainsIn(next),
    };
  }

  async function editLocPage() {
    const id = data.item.itemName;
    const page = `/items/${id}`;
    if (!data.authUser) {
      const ref = turl(page);
      return nav(`/login?r=${ref}`);
    }
    // setLoading(true);
    if (data.mdPage) {
      return nav(`${page}/edit-page`);
    }

    const result = await callApi(editPageApi, {
      input: {
        game: [],
        target: { _: "item", itemName: id },
      },
      realm,
    });

    switch (result.t) {
      case "success":
        // setLoading(false);
        return nav(turl(`${page}/edit-page`));
      case "error":
        console.error(result);

        // setLoading(false);
        break;
    }
  }

  const breadcrumbList = [
    { title: t({ en: "Items", ru: "Предметы" }), url: turl("/items") },
    {
      title: `${t(data.item.name)} ${
        data.item.addName.en ? `[${t(data.item.addName)}]` : ""
      }`.trim(),
      url: turl(`/items/${data.item.itemName}`),
    },
  ];

  const rowsContains:
    | {
        item: {
          id: number;
          itemName: string;
          name: string;
          icon: string;
        };
        count: number;
        chance: number;
      }[]
    | ReactElement = [];

  for (const c of data.item.contains) {
    for (const item of c.items) {
      rowsContains.push({
        item: item.item,
        count: item.count,
        chance: c.chance,
      });
    }
  }

  return (
    <div>
      <div className="content">
        <Breadcrumb items={breadcrumbList} />
        <div className="columns">
          <div className="column is-three-quarters">
            <PageTitle>
              {itemName({
                name: t(data.item.name),
                addName: t(data.item.addName),
              })}
            </PageTitle>
            <div>
              <div
                className="item-box"
                style={{ background: "#292624", padding: 10 }}
              >
                <ItemInfoBox item={data.item} />
              </div>
            </div>

            {data.item.contains.length > 0 && (
              <div className="columns">
                <div className="column">
                  <Heading3>
                    {t({
                      en: "When using it, you can get",
                      ru: "При использовании вы можете получить",
                    })}
                    :
                  </Heading3>
                  {data.item.contains.map((c, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      {c.items.map((item, j) => (
                        <div key={j}>
                          <ItemLink item={item.item} count={item.count} />
                        </div>
                      ))}
                      <div>{c.chance}%</div>
                    </div>
                  ))}
                </div>
                <div className="column"></div>
              </div>
            )}

            {data.item.sets.length > 0 && (
              <div>
                <Heading3>
                  {t({
                    en: "Sets",
                    ru: "Является частью сетов",
                  })}
                  :
                </Heading3>

                {data.item.sets.map((set, i) => (
                  <div className="columns" key={i}>
                    <div
                      className="column"
                      style={{
                        display: "flex",
                        columnGap: "10px",
                        flexDirection: "row",
                      }}
                    >
                      <div style={{ marginTop: "5px" }}>
                        <Icon icon={set.icon} />
                      </div>
                      <div>
                        <div style={{ color: "#fff", fontWeight: "bold" }}>
                          {set.name}
                        </div>
                        <div style={{ fontSize: "14px" }}>{set.desc}</div>
                      </div>
                    </div>
                    <div className="column">
                      {set.items.slotHead.map((item, i) => (
                        <div key={i} style={{ paddingBottom: "5px" }}>
                          <ItemLink item={item} />
                        </div>
                      ))}
                      {set.items.slotChest.map((item, i) => (
                        <div key={i} style={{ paddingBottom: "5px" }}>
                          <ItemLink item={item} />
                        </div>
                      ))}
                      {set.items.slotLegs.map((item, i) => (
                        <div key={i} style={{ paddingBottom: "5px" }}>
                          <ItemLink item={item} />
                        </div>
                      ))}
                      {set.items.slotFeet.map((item, i) => (
                        <div key={i} style={{ paddingBottom: "5px" }}>
                          <ItemLink item={item} />
                        </div>
                      ))}
                      {set.items.slotGloves.map((item, i) => (
                        <div key={i} style={{ paddingBottom: "5px" }}>
                          <ItemLink item={item} />
                        </div>
                      ))}
                      {set.items.slotLhand.map((item, i) => (
                        <div key={i} style={{ paddingBottom: "5px" }}>
                          <ItemLink item={item} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {data.item.specialAbility.length > 0 && (
              <div>
                <Heading3>
                  {t({
                    en: "Special Abilities",
                    ru: "Можно вставить следующте свойства",
                  })}
                  :
                </Heading3>
                <div className="columns">
                  <div className="column">
                    {data.item.specialAbility.map((weaponSA, i) => (
                      <div key={i} style={{ paddingBottom: "5px" }}>
                        <ItemLink
                          item={{
                            ...weaponSA,
                            name: `${weaponSA.name}`,
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {data.mdPage.content && (
              <>
                <Heading3>
                  {t({
                    en: "Wiki",
                    ru: "Wiki",
                  })}
                </Heading3>
                <MdContent>{data.mdPage.content}</MdContent>
              </>
            )}

            {/* {data.item.dropList.length > 0 && (
              <DropListBlock dropList={data.item.dropList} />
            )} */}
          </div>
          <div className="column">
            <InfoBox
              title={t({
                en: "Item Facts",
                ru: "Краткая информация",
              })}
            >
              <Facts
                facts={[
                  {
                    name: "Item ID",
                    value: data.item.id,
                  },
                  {
                    name: "Англ. название",
                    value: itemName({
                      name: data.item.name.en,
                      addName: data.item.addName.en,
                    }),
                    show: realm.lang === "ru",
                  },
                  {
                    name: t({ en: "Base price", ru: "Базовая цена" }),
                    value: <AdenaValue value={data.item.defaultPrice ?? 0} />,
                  },
                  {
                    name: (
                      <span>
                        <CrystalName type={data.item.crystalType ?? ""} /> count
                      </span>
                    ),
                    value: (
                      <CrystalValue
                        type={data.item.crystalType ?? ""}
                        value={data.item.crystalCount ?? 0}
                      />
                    ),
                    show: data.item.crystalCount != 0,
                  },
                ]}
              />
              <InputMD value={`:item[${data.item.itemName}]`} />
            </InfoBox>
            {data.item.crystalCount ? (
              <InfoBox
                title={t({
                  en: "Crystal Price Calc",
                  ru: "Калькулятор Кристаллов",
                })}
              >
                <CrystalCalc
                  crystalType={data.item.crystalType ?? ""}
                  crystalCount={data.item.crystalCount}
                  defaultPrice={data.item.defaultPrice ?? 0}
                />
              </InfoBox>
            ) : null}
          </div>
        </div>
      </div>
      <div style={{ marginTop: 20, overflowX: "auto" }} />
      <Tabs
        current={params["*"] ?? ""}
        link={(id) => turl(stripSlashes(`/items/${data.item.itemName}/${id}`))}
        tabs={[
          {
            id: "comments",
            title: t({ en: "Comments", ru: "Комментарии" }),
            body: () => (
              <div>
                <Comments
                  comments={data.comments}
                  authUser={data.authUser}
                  subject={{ objType: "item", objUname: data.item.itemName }}
                />
              </div>
            ),
          },
          ...renderRecipeInfo(data.item.recipe, turl),
          {
            id: "",
            title: t({ en: "Drop", ru: "Дроп" }),
            count: data.item.dropList.length,
            body: () => (
              <Table
                data={SORTER_DROP.apply(data.item.dropList, drop.join(","))}
                id={(_drop, i) => i}
                cols={[
                  {
                    title: "",
                    cell: (drop) =>
                      drop.npc.race?.icon && <Icon icon={drop.npc.race.icon} />,
                    thPadding: "0",
                    padding: "8px 0 8px 8px",
                    width: 32 + 12,
                  },
                  {
                    title: (
                      <SortColumnName name="name" by={sortByDrop()}>
                        {t({ en: "Name", ru: "Имя" })}
                      </SortColumnName>
                    ),
                    cell: (drop) => (
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <NpcLink npc={drop.npc} showLvl={false} />
                        <div style={{ fontSize: 12, maxWidth: 400 }}>
                          {getLocList(drop.npc.locs)}
                        </div>
                      </div>
                    ),
                  },
                  {
                    title: (
                      <SortColumnName name="lvl" by={sortByDrop()}>
                        {t({ en: "Level", ru: "Уровень" })}
                      </SortColumnName>
                    ),
                    cell: (drop) => <>{drop.npc.level}</>,
                  },
                  {
                    title: "Rate",
                    cell: ({ npc }) => (
                      <NpcHpRate skills={npc.skills} game={realm.game} />
                    ),
                  },
                  {
                    title: (
                      <SortColumnName name="chance" by={sortByDrop()}>
                        {t({ en: "Chance", ru: "Шанс" })}
                      </SortColumnName>
                    ),
                    cell: (drop) => (
                      <>{parseFloat(drop.chance.toPrecision(4))}%</>
                    ),
                  },
                  {
                    title: (
                      <SortColumnName name="count" by={sortByDrop()}>
                        {t({ en: "Count", ru: "Кол-во" })}
                      </SortColumnName>
                    ),
                    cell: (drop) => {
                      let ret = drop.countMin.toString();
                      if (drop.countMin !== drop.countMax) {
                        ret += " - " + drop.countMax.toString();
                      }
                      return ret;
                    },
                  },
                ]}
              />
            ),
          },
          {
            id: "spoil",
            title: t({ en: "Spoil", ru: "Спойл" }),
            count: data.item.spoilList.length,
            body: () => (
              <Table
                data={SORTER_DROP.apply(data.item.spoilList, spoil.join(","))}
                id={(_spoil, i) => i}
                cols={[
                  {
                    title: "",
                    cell: (spoil) =>
                      spoil.npc.race?.icon && (
                        <Icon icon={spoil.npc.race.icon} />
                      ),
                    thPadding: "0",
                    padding: "8px 0 8px 8px",
                    width: 32 + 12,
                  },
                  {
                    title: (
                      <SortColumnName name="name" by={sortBySpoil()}>
                        {t({ en: "Name", ru: "Имя" })}
                      </SortColumnName>
                    ),
                    cell: (spoil) => (
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <NpcLink npc={spoil.npc} showLvl={false} />
                        <div style={{ fontSize: 12, maxWidth: 400 }}>
                          {getLocList(spoil.npc.locs)}
                        </div>
                      </div>
                    ),
                  },
                  {
                    title: (
                      <SortColumnName name="lvl" by={sortBySpoil()}>
                        {t({ en: "Level", ru: "Уровень" })}
                      </SortColumnName>
                    ),
                    cell: (drop) => <>{drop.npc.level}</>,
                  },
                  {
                    title: "Rate",
                    cell: ({ npc }) => (
                      <NpcHpRate skills={npc.skills} game={realm.game} />
                    ),
                  },
                  {
                    title: (
                      <SortColumnName name="chance" by={sortBySpoil()}>
                        {t({ en: "Chance", ru: "Шанс" })}
                      </SortColumnName>
                    ),
                    cell: (spoil) => (
                      <>{parseFloat(spoil.chance.toPrecision(4))}%</>
                    ),
                  },
                  {
                    title: (
                      <SortColumnName name="count" by={sortBySpoil()}>
                        {t({ en: "Count", ru: "Кол-во" })}
                      </SortColumnName>
                    ),
                    cell: (spoil) => {
                      let ret = spoil.countMin.toString();
                      if (spoil.countMin !== spoil.countMax) {
                        ret += " - " + spoil.countMax.toString();
                      }
                      return ret;
                    },
                  },
                ]}
              />
            ),
          },
          {
            id: "herbs",
            title: t({ en: "Drop", ru: "Дроп" }),
            count: data.item.herbList.length,
            body: () => (
              <Table
                data={SORTER_DROP.apply(data.item.herbList, herbs.join(","))}
                id={(_herb, i) => i}
                cols={[
                  {
                    title: "",
                    cell: (herb) =>
                      herb.npc.race?.icon && <Icon icon={herb.npc.race.icon} />,
                    thPadding: "0",
                    padding: "8px 0 8px 8px",
                    width: 32 + 12,
                  },
                  {
                    title: (
                      <SortColumnName name="name" by={sortByHerbs()}>
                        {t({ en: "Name", ru: "Имя" })}
                      </SortColumnName>
                    ),
                    cell: (herb) => (
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <NpcLink npc={herb.npc} showLvl={false} />
                        <div style={{ fontSize: 12, maxWidth: 400 }}>
                          {getLocList(herb.npc.locs)}
                        </div>
                      </div>
                    ),
                  },
                  {
                    title: (
                      <SortColumnName name="lvl" by={sortByHerbs()}>
                        {t({ en: "Level", ru: "Уровень" })}
                      </SortColumnName>
                    ),
                    cell: (drop) => <>{drop.npc.level}</>,
                  },
                  {
                    title: "Rate",
                    cell: ({ npc }) => (
                      <NpcHpRate skills={npc.skills} game={realm.game} />
                    ),
                  },
                  {
                    title: (
                      <SortColumnName name="chance" by={sortByHerbs()}>
                        {t({ en: "Chance", ru: "Шанс" })}
                      </SortColumnName>
                    ),
                    cell: (herb) => (
                      <>{parseFloat(herb.chance.toPrecision(4))}%</>
                    ),
                  },
                  {
                    title: (
                      <SortColumnName name="count" by={sortByHerbs()}>
                        {t({ en: "Count", ru: "Кол-во" })}
                      </SortColumnName>
                    ),
                    cell: (herb) => {
                      let ret = herb.countMin.toString();
                      if (herb.countMin !== herb.countMax) {
                        ret += " - " + herb.countMax.toString();
                      }
                      return ret;
                    },
                  },
                ]}
              />
            ),
          },
          {
            id: "shops",
            title: t({ en: "Shops", ru: "Магазины" }),
            count: data.item.sellList.length,
            body: () => (
              <Table
                data={SORTER_SHOP.apply(data.item.sellList, shop.join(","))}
                id={(_shop, i) => i}
                cols={[
                  {
                    title: (
                      <SortColumnName name="name" by={sortByShop()}>
                        {t({ en: "Name", ru: "Имя" })}
                      </SortColumnName>
                    ),
                    cell: (merch) => (
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <NpcLink npc={merch.npc} showLvl={false} />
                        <div style={{ fontSize: 12 }}>
                          {getLocList(merch.npc.locs)}
                        </div>
                      </div>
                    ),
                  },
                  {
                    title: (
                      <SortColumnName name="tax" by={sortByShop()}>
                        {t({ en: "Price", ru: "Цена" })}
                      </SortColumnName>
                    ),
                    cell: (merch) => (
                      <AdenaValue
                        value={Math.round(
                          (data.item.defaultPrice ?? 0) * (1 + merch.tax / 100),
                        )}
                      />
                    ),
                  },
                  {
                    title: t({ en: "Cry Price", ru: "Цена за кристалл" }),
                    hide: data.item.crystalType === "none",
                    cell: (merch) => (
                      <>
                        <AdenaValue
                          value={Math.round(
                            ((data.item.defaultPrice ?? 0) *
                              (1 + merch.tax / 100)) /
                              (data.item.crystalCount ?? 0),
                          )}
                        />{" "}
                        (
                        {Math.round(
                          (data.item.defaultPrice ?? 0) /
                            (data.item.crystalCount ?? 0),
                        )}
                        )
                      </>
                    ),
                  },
                ]}
              />
            ),
          },
          {
            id: "in-quests",
            title: t({ en: "In Quests", ru: "В квестах" }),
            count: data.item.inQuests.length,
            body: () => (
              <Table
                data={data.item.inQuests}
                id={(_quest, i) => i}
                cols={[
                  {
                    title: t({ en: "Name", ru: "Имя" }),
                    cell: (quest) => <QuestLink quest={quest} />,
                  },
                  {
                    title: t({ en: "Level", ru: "Уровень" }),
                    cell: (quest) => (
                      <>
                        {quest.minLevel || ""}
                        {quest.minLevel && quest.maxLevel ? "-" : null}
                        {quest.maxLevel || ""}
                      </>
                    ),
                  },
                  {
                    title: t({ en: "Reward", ru: "Награда" }),
                    cell: (quest) => <QuestRewardList reward={quest.reward} />,
                  },
                  {
                    title: t({ en: "Race", ru: "Раса" }),
                    cell: (quest) => (
                      <QuestRaceList races={quest.requirements.races} />
                    ),
                  },
                  {
                    title: t({ en: "Class", ru: "Класс" }),
                    cell: (quest) => (
                      <QuestProfList profs={quest.requirements.occupation} />
                    ),
                  },
                ]}
              />
            ),
          },
          {
            id: "reward",
            title: t({ en: "Reward", ru: "Награда" }),
            count: data.item.questReward.length,
            body: () => (
              <Table
                data={data.item.questReward}
                id={(_quest, i) => i}
                cols={[
                  {
                    title: t({ en: "Name", ru: "Имя" }),
                    cell: (quest) => <QuestLink quest={quest} />,
                  },
                  {
                    title: t({ en: "Level", ru: "Уровень" }),
                    cell: (quest) => (
                      <>
                        {quest.minLevel || ""}
                        {quest.minLevel && quest.maxLevel ? "-" : null}
                        {quest.maxLevel || ""}
                      </>
                    ),
                  },
                  {
                    title: t({ en: "Reward", ru: "Награда" }),
                    cell: (quest) => <QuestRewardList reward={quest.reward} />,
                  },
                  {
                    title: t({ en: "Race", ru: "Раса" }),
                    cell: (quest) => (
                      <QuestRaceList races={quest.requirements.races} />
                    ),
                  },
                  {
                    title: t({ en: "Class", ru: "Класс" }),
                    cell: (quest) => (
                      <QuestProfList profs={quest.requirements.occupation} />
                    ),
                  },
                ]}
              />
            ),
          },
          {
            id: "craft",
            title: t({ en: "Craft", ru: "Крафт" }),
            count: data.item.product.filter(
              (prod) =>
                prod.recipe.item.dropList.length ||
                prod.recipe.item.spoilList.length ||
                prod.recipe.item.sellList.length ||
                prod.recipe.item.exchange_from.length ||
                prod.recipe.item.questReward.length,
            ).length,
            body: () => (
              <div className="columns" style={{ gap: 2 }}>
                {data.item.product
                  .filter(
                    (prod) =>
                      prod.recipe.item.dropList.length ||
                      prod.recipe.item.spoilList.length ||
                      prod.recipe.item.sellList.length ||
                      prod.recipe.item.exchange_from.length ||
                      prod.recipe.item.questReward.length,
                  )
                  .map((prod, i) => (
                    <div
                      className="column"
                      style={{ background: "#292524", marginBottom: 2 }}
                      key={i}
                    >
                      <RecipeItem recipe={prod.recipe} turl={turl} t={t} />
                    </div>
                  ))}
              </div>
            ),
          },
          {
            id: "in-recipe",
            title: t({ en: "In Recipe", ru: "В рецепте" }),
            count: data.item.inRecipe.length,
            body: () => (
              <Table
                data={data.item.inRecipe}
                id={(_inRecipe, i) => i}
                cols={[
                  {
                    title: "",
                    cell: (item) => <Icon icon={item.icon} />,
                    thPadding: "0",
                    padding: "8px 0 8px 8px",
                    width: 32 + 12,
                  },
                  {
                    title: (
                      <SortColumnName name="name" by={sortBySpoil()}>
                        {t({ en: "Name", ru: "Имя" })}
                      </SortColumnName>
                    ),
                    cell: (item) => <ItemLink item={item} showIcon={false} />,
                  },
                ]}
              />
            ),
          },
          {
            id: "exchange-from",
            title: t({ en: "Exchange From", ru: "Обменять с" }),
            count: data.item.exchange_from.length,
            body: () => (
              <Table
                data={data.item.exchange_from}
                id={(_ms, i) => i}
                cols={[
                  {
                    title: t({ en: "NPCs", ru: "НПЦ" }),
                    cell: (ms) => <div>{getNpcList(ms.npcs)}</div>,
                    width: "50%",
                  },
                  {
                    title: t({
                      en: "Required Items",
                      ru: "Необходимые предметы",
                    }),
                    cell: (ms) => (
                      <div>
                        {ms.requiredItems.map((req, i) => (
                          <div key={i}>
                            <ItemLink item={req.item} />
                            {req.count && ` x${req.count} `}
                          </div>
                        ))}
                      </div>
                    ),
                  },
                ]}
              />
            ),
          },
          {
            id: "exchange-for",
            title: t({ en: "Exchange For", ru: "Обменять на" }),
            count: data.item.exchange_for.length,
            body: () => (
              <Table
                data={data.item.exchange_for}
                id={(_ms, i) => i}
                cols={[
                  {
                    title: t({ en: "NPCs", ru: "НПЦ" }),
                    cell: (ms) => <div>{getNpcList(ms.npcs)}</div>,
                    width: "50%",
                  },
                  {
                    title: t({
                      en: "Required Items",
                      ru: "Необходимые предметы",
                    }),
                    cell: (ms) => (
                      <div>
                        {ms.requiredItems.map((req, i) => (
                          <div key={i}>
                            <ItemLink item={req.item} count={req.count} />
                          </div>
                        ))}
                      </div>
                    ),
                  },
                  {
                    title: t({
                      en: "Result Items",
                      ru: "Получаемые предметы",
                    }),
                    cell: (ms) => (
                      <div>
                        {ms.resultItems.map((req, i) => (
                          <div key={i}>
                            <ItemLink item={req.item} count={req.count} />
                          </div>
                        ))}
                      </div>
                    ),
                  },
                ]}
              />
            ),
          },
          {
            id: "contains-in",
            title: t({ en: "Contain In", ru: "Содержится в" }),
            count: data.item.containsIn.length,
            body: () => (
              <Table
                data={SORTER_CONTAIN_IN.apply(
                  data.item.containsIn,
                  containsIn.join(","),
                )}
                id={(_conIn, i) => i}
                cols={[
                  {
                    title: "",
                    cell: (contain) => (
                      <EntityTooltip
                        what={{ t: "item", uname: contain.item.itemName }}
                      >
                        {({ setReference, getReferenceProps }) => (
                          <div
                            ref={setReference}
                            {...getReferenceProps({
                              style: {
                                display: "flex",
                              },
                            })}
                          >
                            <Icon32 icon={contain.item.icon} />
                          </div>
                        )}
                      </EntityTooltip>
                    ),
                    thPadding: "0",
                    padding: "8px 0 8px 8px",
                    width: 32 + 12,
                  },
                  {
                    title: t({ en: "Name", ru: "Название" }),
                    cell: (contain) => (
                      <>
                        <div className="text-white font-medium">
                          <Link to={turl(`/items/${contain.item.itemName}`)}>
                            {contain.item.name}
                          </Link>
                        </div>
                      </>
                    ),
                  },
                  {
                    title: (
                      <SortColumnName name="chance" by={sortByContainsIn()}>
                        {t({ en: "Chance", ru: "Шанс" })}
                      </SortColumnName>
                    ),
                    cell: (contain) => contain.chance + "%",
                  },
                  {
                    title: (
                      <SortColumnName name="count" by={sortByContainsIn()}>
                        {t({ en: "Count", ru: "Кол-во" })}
                      </SortColumnName>
                    ),
                    cell: (contain) => contain.count,
                  },
                ]}
              />
            ),
          },
          {
            id: "enchanting",
            title: t({ en: "Enchanting", ru: "Улучшение" }),
            count: data.item.enchantBonus.length,
            body: () => (
              <Table
                data={data.item.enchantBonus}
                id={(_enc, i) => i}
                cols={[
                  {
                    title: "",
                    cell: (ench) => (
                      <span style={{ color: "white" }}>+{ench.level}</span>
                    ),
                    width: 20,
                  },
                  {
                    title: t({ en: "Item", ru: "Предмет" }),
                    cell: (ench) => (
                      <ItemLink
                        item={{
                          icon: data.item.icon,
                          itemName: data.item.itemName,
                          name: data.item.name.en,
                          addName: data.item.addName.en,
                        }}
                      />
                    ),
                  },
                  {
                    title: t({ en: "P. Atk.", ru: "Физ. Атк." }),
                    cell: (ench) => ench.pAtk,
                    hide: data.item.type !== "weapon",
                  },
                  {
                    title: t({ en: "M. Atk.", ru: "Маг. Атк." }),
                    cell: (ench) => ench.mAtk,
                    hide: data.item.type !== "weapon",
                  },
                  {
                    title: t({ en: "P. Def.", ru: "Физ. Защита." }),
                    cell: (ench) => ench.pDef,
                    hide: data.item.type !== "armor",
                  },
                  {
                    title: t({ en: "m. Def.", ru: "Маг. Защита." }),
                    cell: (ench) => ench.mDef,
                    hide: data.item.type !== "accessary",
                  },
                  {
                    title: t({ en: "Crystals", ru: "Кристаллы" }),
                    cell: (ench) => ench.crystals[0] + " / " + ench.crystals[1],
                  },
                  {
                    title: t({ en: "Chance", ru: "Шанс" }),
                    cell: (ench) => ench.chance + "%",
                  },
                ]}
              />
            ),
          },
        ]}
      />
    </div>
  );
}

type RecipeTree = {
  materialList: Array<{
    count: number;
    item: {
      id: number;
      icon: string;
      itemName: string;
      name: string;
      product?: Array<{ recipe: RecipeTree }>;
    };
  }>;
};

function RecipeItem(props: {
  recipe: LoaderDataOk["item"]["recipe"][number];
  turl: ReturnType<typeof useTurl>;
  t: ReturnType<typeof useT>;
}) {
  const { recipe, t, turl } = props;
  return (
    <div className="content" style={{ paddingTop: 1 }}>
      <Heading2>Recipe {recipe.successRate}%</Heading2>
      <ItemLink item={recipe.item} />
      <div>
        {t({ en: "Success rate", ru: "Шанс успешного крафта" })}:{" "}
        {props.recipe.successRate}%
      </div>
      <div>
        {t({ en: "MP consume", ru: "Затраты MP" })}: {props.recipe.mpConsume}
      </div>
      <Heading4>Recipe Result</Heading4>
      <ul>
        {recipe.productList.map((prod, i) => {
          return (
            <li key={i}>
              <div className="recipe-item">
                <span className="recipe-item-count">{prod.count}</span>
                <Icon size={16} icon={prod.item.icon} alt={prod.item.name} />
                <Link
                  className="recipe-item-name"
                  to={turl("/items/" + prod.item.itemName)}
                >
                  {prod.item.name}
                </Link>
              </div>
            </li>
          );
        })}
      </ul>

      <Heading4>Recipe Materials</Heading4>
      <div className="recipe">{materialTree(recipe)}</div>

      <CraftLink recId={recipe.item.id} />
    </div>
  );
}

function renderRecipeInfo(
  recipes: LoaderDataOk["item"]["recipe"],
  turl: ReturnType<typeof useTurl>,
) {
  return recipes.map<TabSpec>((recipe) => {
    return {
      id: "recipe-" + recipe.successRate,
      title: `Recipe (${recipe.successRate}%)`,
      body: () => {
        return (
          <div className="content" style={{ paddingTop: 0 }}>
            <Heading2>Recipe Result</Heading2>
            <ul>
              {recipe.productList.map((prod, i) => {
                return (
                  <li key={i}>
                    <div className="recipe-item">
                      <span className="recipe-item-count">{prod.count}</span>
                      <Icon
                        size={16}
                        icon={prod.item.icon}
                        alt={prod.item.name}
                      />
                      <Link
                        className="recipe-item-name"
                        to={turl("/items/" + prod.item.itemName)}
                      >
                        {prod.item.name}
                      </Link>
                    </div>
                  </li>
                );
              })}
            </ul>

            <Heading2>Recipe Materials</Heading2>
            <div className="recipe">{materialTree(recipe)}</div>
          </div>
        );
      },
    };
  });
}

function MaterialItem(props: { prod: RecipeTree["materialList"][number] }) {
  const turl = useTurl();
  const [open, setOpen] = useState(false);

  const prod = props.prod;
  return (
    <li>
      <div className="recipe-item">
        <span className="recipe-item-count">{prod.count}</span>
        <Icon size={16} icon={prod.item.icon} alt={prod.item.name} />
        <Link
          className="recipe-item-name"
          to={turl("/items/" + prod.item.itemName)}
        >
          {prod.item.name}
        </Link>
        {prod.item.product?.[0] && (
          <span
            onClick={() => setOpen((prev) => !prev)}
            className="recipe-item-open"
          >
            {open ? "CLOSE" : "OPEN"}
          </span>
        )}
      </div>
      {open && prod.item.product?.[0] && (
        <div>{materialTree(prod.item.product[0].recipe)}</div>
      )}
    </li>
  );
}

function materialTree(recipe: RecipeTree) {
  return (
    <ul>
      {recipe.materialList.map((prod, i) => {
        return <MaterialItem key={i} prod={prod} />;
      })}
    </ul>
  );
}
